<script setup>
import { ref, onMounted } from 'vue';
import { listUpdates, createUpdate } from '../db/updates.js';
import { currentUser } from '../db/auth.js';
import UpdateStatusModal from '../components/UpdateStatusModal.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import { check } from '@tauri-apps/plugin-updater';
import { relaunch } from '@tauri-apps/plugin-process';
import { getVersion } from '@tauri-apps/api/app';

const updates = ref([]);
const loading = ref(false);
const isAdmin = ref(false);
const currentVersion = ref('');

// Modal State
const showModal = ref(false);
const modalStatus = ref('loading');
const modalTitle = ref('');
const modalMessage = ref('');

// Confirm Modal State
const showConfirmModal = ref(false);
const confirmTitle = ref('');
const confirmMessage = ref('');
const confirmAction = ref(null);

onMounted(async () => {
  loading.value = true;
  updates.value = await listUpdates();

  const user = currentUser();
  const role = String(user?.role || "").toLowerCase();
  isAdmin.value = role === "admin" || role === "superadmin" || user?.role_id === 1;

  try {
    const v = await getVersion();
    currentVersion.value = v || '0.1.2'; // Fallback manual si falla
  } catch (e) {
    console.error('Error getting version:', e);
    currentVersion.value = '0.1.2'; // Fallback manual
  }

  loading.value = false;
});

const checkForUpdates = async () => {
  // Reset modal state
  modalStatus.value = 'loading';
  modalTitle.value = 'Buscando actualizaciones...';
  modalMessage.value = 'Conectando con el servidor de versiones...';
  showModal.value = true;

  try {
    const update = await check();

    if (update) {
      modalTitle.value = 'Actualización encontrada';
      modalMessage.value = `Versión ${update.version} disponible. Descargando e instalando...`;

      // Descargar e instalar
      await update.downloadAndInstall((event) => {
        switch (event.event) {
          case 'Started':
            modalMessage.value = 'Iniciando descarga...';
            break;
          case 'Progress':
            modalMessage.value = `Descargando: ${event.data.chunkLength} bytes recibidos...`;
            break;
          case 'Finished':
            modalMessage.value = 'Descarga completada. Instalando...';
            break;
        }
      });

      modalStatus.value = 'success';
      modalTitle.value = 'Actualización completada';
      modalMessage.value = 'La aplicación se reiniciará para aplicar los cambios.';

      setTimeout(async () => {
        await relaunch();
      }, 3000);

    } else {
      modalStatus.value = 'success'; // Usamos success para indicar que "todo está bien" (está al día)
      modalTitle.value = 'Sistema Actualizado';
      modalMessage.value = 'Estás utilizando la versión más reciente.';
    }
  } catch (error) {
    console.error('Error al buscar actualizaciones:', error);

    // Personalizar mensajes de error comunes
    let friendlyMessage = '';
    const errorStr = String(error);

    if (errorStr.includes('Could not fetch a valid release JSON') || errorStr.includes('404')) {
      friendlyMessage =
        'No se pudo obtener la información de la última versión (latest.json).\n\n' +
        'En esta instalación la app espera encontrarlo en:\n' +
        'http://localhost:9000/tranquero-updates/latest.json\n\n' +
        'Si ves 404, significa que el archivo no existe todavía en MinIO. ' +
        'Después de compilar, sube los artifacts y genera el manifest con:\n' +
        'npm run publish:updater:minio';
    } else if (errorStr.toLowerCase().includes('signature') || errorStr.toLowerCase().includes('not valid')) {
      friendlyMessage =
        'La firma de la actualización no es válida. ' +
        'Verifica que la pubkey en tauri.conf.json coincida con la clave privada usada en GitHub Actions.';
    } else if (errorStr.includes('Network Error') || errorStr.includes('network')) {
      friendlyMessage = 'Error de conexión. Verifica tu acceso a internet.';
    } else {
      friendlyMessage = `No se pudo buscar actualizaciones. ${error.message || error}`;
    }

    modalStatus.value = 'error';
    modalTitle.value = 'No se pudo comprobar actualizaciones';
    modalMessage.value = friendlyMessage;
  } finally {
    // Limpieza o estado final si fuera necesario
  }
}

const registerCurrentVersion = () => {
  const version = currentVersion.value || '0.1.3';
  confirmTitle.value = 'Registrar Versión';
  confirmMessage.value = `¿Registrar la versión actual (v${version}) en el historial de la base de datos?`;
  confirmAction.value = async () => {
    loading.value = true;
    try {
      await createUpdate({
        version: version,
        title: 'Actualización Automática v0.1.3',
        description: `Esta versión v0.1.3 verifica el funcionamiento del sistema de actualizaciones automáticas.

✨ Novedades
- Prueba de flujo completo de actualización (GitHub -> App).
- Corrección visual en el botón de registro de versiones.
- Mejoras de estabilidad general.`,
        release_date: new Date().toISOString().split('T')[0],
        release_time: new Date().toLocaleTimeString('en-US', { hour12: false })
      });
      // Refresh list
      updates.value = await listUpdates();

      // Mostrar éxito usando el UpdateStatusModal en lugar de alert
      modalStatus.value = 'success';
      modalTitle.value = 'Versión Registrada';
      modalMessage.value = `La versión v${version} ha sido registrada correctamente en la base de datos.`;
      showModal.value = true;

    } catch (error) {
      console.error(error);
      modalStatus.value = 'error';
      modalTitle.value = 'Error';
      modalMessage.value = 'Error al registrar versión: ' + (error.message || error);
      showModal.value = true;
    } finally {
      loading.value = false;
      showConfirmModal.value = false;
    }
  };
  showConfirmModal.value = true;
};

function closeModal() {
  showModal.value = false;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
</script>

<template>
  <div class="page-wrap">
    <UpdateStatusModal :show="showModal" :status="modalStatus" :title="modalTitle" :message="modalMessage"
      @close="closeModal" />

    <ConfirmModal :show="showConfirmModal" :title="confirmTitle" :message="confirmMessage" @confirm="confirmAction"
      @cancel="showConfirmModal = false" />

    <header class="page-header">
      <div class="header-content">
        <div>
          <h1 class="title">Actualizaciones</h1>
          <p class="subtitle">Historial de versiones y cambios del sistema</p>
        </div>
        <div style="display: flex; gap: 1rem;">
          <button v-if="isAdmin" class="btn-check btn-secondary" @click="registerCurrentVersion">
            <span class="material-symbols-rounded">add_circle</span>
            Registrar v{{ currentVersion }}
          </button>
          <button class="btn-check" @click="checkForUpdates">
            <span class="material-symbols-rounded">update</span>
            Buscar actualizaciones
          </button>
        </div>
      </div>
    </header>

    <section class="content" v-if="!loading">
      <div v-if="updates.length === 0" class="empty-state">
        <span class="material-symbols-rounded icon-large">history</span>
        <p>No hay historial de actualizaciones registrado.</p>
      </div>

      <div class="timeline" v-else>
        <div v-for="(update, index) in updates" :key="update.id_app_updates || index" class="timeline-item">
          <div class="timeline-marker"></div>
          <div class="timeline-content card">
            <div class="update-header">
              <span class="version-badge">v{{ update.version }}</span>
              <span class="update-date">{{ formatDate(update.release_date) }} <span v-if="update.release_time"> - {{
                update.release_time }}</span></span>
            </div>
            <h3 class="update-title">{{ update.title }}</h3>
            <div class="update-description">
              <p>{{ update.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-else class="loading-state">
      <div class="spinner"></div>
      <p>Cargando historial...</p>
    </div>
  </div>
</template>

<style>
.page-wrap {
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.page-wrap .page-header {
  margin-bottom: 3rem;
}

.page-wrap .header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.page-wrap .title {
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #7c5cff 0%, #DFC05E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 0.5rem 0;
}

.page-wrap .subtitle {
  color: var(--muted);
  font-size: 1rem;
  margin: 0;
}

.page-wrap .btn-check {
  appearance: none !important;
  -webkit-appearance: none !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.85rem 1.75rem;
  background: rgba(198, 146, 61, 0.1) !important;
  border: 1px solid var(--accent) !important;
  border-radius: 12px !important;
  color: var(--accent) !important;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.1);
  text-decoration: none;
}

.page-wrap .btn-check:hover {
  transform: translateY(-2px);
  background: var(--accent) !important;
  color: white !important;
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.3);
}

.page-wrap .btn-check:active {
  transform: translateY(0);
}

.page-wrap .btn-check .material-symbols-rounded {
  font-size: 1.25rem;
}

.page-wrap .btn-secondary {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: var(--text) !important;
}

.page-wrap .btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.page-wrap .timeline {
  position: relative;
  padding-left: 2rem;
  border-left: 2px solid rgba(198, 146, 61, 0.1);
}

.page-wrap .timeline-item {
  position: relative;
  margin-bottom: 2.5rem;
}

.page-wrap .timeline-marker {
  position: absolute;
  left: -2.6rem;
  top: 1.5rem;
  width: 1.2rem;
  height: 1.2rem;
  background: var(--surface);
  border: 3px solid #7c5cff;
  border-radius: 50%;
  z-index: 1;
  box-shadow: 0 0 0 4px var(--bg);
}

.page-wrap .card {
  background: var(--surface);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease;
}

.page-wrap .card:hover {
  transform: translateY(-2px);
  border-color: rgba(198, 146, 61, 0.3);
}

.page-wrap .update-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.page-wrap .version-badge {
  background: rgba(198, 146, 61, 0.15);
  color: #7c5cff;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  border: 1px solid rgba(198, 146, 61, 0.2);
}

.page-wrap .update-date {
  color: var(--muted);
  font-size: 0.9rem;
}

.page-wrap .update-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
  color: var(--text);
}

.page-wrap .update-description {
  color: var(--text);
  opacity: 0.9;
  line-height: 1.6;
  white-space: pre-line;
}

.page-wrap .empty-state {
  text-align: center;
  padding: 6rem 1rem;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.02);
  border-radius: 20px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

.page-wrap .icon-large {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.3;
  display: block;
}

.page-wrap .loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  gap: 1rem;
  color: var(--muted);
}

.page-wrap .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(198, 146, 61, 0.1);
  border-top-color: #7c5cff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 600px) {
  .page-wrap {
    padding: 1.5rem 1rem;
  }

  .page-wrap .timeline {
    padding-left: 1.5rem;
  }

  .page-wrap .timeline-marker {
    left: -2.15rem;
    width: 1rem;
    height: 1rem;
  }
}
</style>
